import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar, Clock, Users, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { formatGusmesterNames } from '../utils/gusmesterDisplay';

interface Booking {
  id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  session_name: string;
  gusmester_name: string;
  session_type: 'regular' | 'special_event';
  gusmesters?: { id: number; name: string }[];
  status: string;
  created_at: string;
}

interface WaitingListEntry {
  id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  session_name: string;
  gusmester_name: string;
  joined_at: string;
  position: number;
}

const MyBookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [waitingList, setWaitingList] = useState<WaitingListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [leavingWaitingList, setLeavingWaitingList] = useState<number | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const [bookingsResponse, waitingListResponse] = await Promise.all([
        axios.get('/api/bookings'),
        axios.get('/api/waiting-list')
      ]);
      setBookings(bookingsResponse.data);
      setWaitingList(waitingListResponse.data);
    } catch (error) {
      setError('Kunne ikke indlæse bookinger');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId: number) => {
    if (!window.confirm('Er du sikker på, at du vil aflyse denne booking?')) {
      return;
    }

    setCancellingId(bookingId);
    try {
      await axios.delete(`/api/bookings/${bookingId}`);
      setBookings(bookings.filter(booking => booking.id !== bookingId));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Kunne ikke aflyse booking');
    } finally {
      setCancellingId(null);
    }
  };

  const handleLeaveWaitingList = async (waitingListId: number) => {
    if (!window.confirm('Er du sikker på, at du vil forlade ventelisten?')) {
      return;
    }

    console.log('Attempting to leave waiting list with ID:', waitingListId);
    setLeavingWaitingList(waitingListId);
    try {
      const response = await axios.delete(`/api/waiting-list/${waitingListId}`);
      console.log('Successfully left waiting list:', response.data);
      setWaitingList(waitingList.filter(entry => entry.id !== waitingListId));
      
      // Refresh the waiting list data to ensure UI is up to date
      await fetchBookings();
    } catch (error: any) {
      console.error('Error leaving waiting list:', error);
      alert(error.response?.data?.error || 'Kunne ikke forlade venteliste');
    } finally {
      setLeavingWaitingList(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  const isUpcoming = (sessionDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const session = new Date(sessionDate);
    return session >= today;
  };

  const isWithinOneDay = (sessionDate: string) => {
    const now = new Date();
    const session = new Date(sessionDate);
    const oneDayAgo = new Date(now);
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    oneDayAgo.setHours(0, 0, 0, 0);
    return session >= oneDayAgo;
  };

  const canCancel = (sessionDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const session = new Date(sessionDate);
    return session > today; // Can only cancel future sessions
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Fejl</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBookings}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg"
          >
            Prøv Igen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mine GUS Bookinger</h1>
        <p className="text-gray-600">Se og administrer dine traditionelle danske sauna session bookinger.</p>
      </div>

      {bookings.filter(booking => isWithinOneDay(booking.session_date)).length === 0 && 
       waitingList.filter(entry => isWithinOneDay(entry.session_date)).length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Ingen bookinger endnu</h3>
          <p className="text-gray-600 mb-4">Du har ikke booket nogen GUS sessioner endnu.</p>
          <a
            href="/sessions"
            className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Gennemse GUS Sessioner
          </a>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Bookings Section */}
          {bookings.filter(booking => isWithinOneDay(booking.session_date)).length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Mine Bookinger</h2>
              <div className="grid gap-6">
                {bookings.filter(booking => isWithinOneDay(booking.session_date)).map((booking) => (
                  <div key={booking.id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {booking.session_name || 'GUS Session'} - {format(new Date(booking.session_date), 'EEEE')}
                            </h3>
                            <p className="text-gray-600">
                              {format(new Date(booking.session_date), 'EEEE, MMMM d, yyyy')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(booking.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                              {booking.status === 'confirmed' ? 'Bekræftet' : 
                               booking.status === 'cancelled' ? 'Aflyst' : 
                               booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                            </span>
                            {isUpcoming(booking.session_date) && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                Kommende
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>{booking.start_time} - {booking.end_time}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            <span>
                              Gusmester: {booking.session_type === 'special_event' && booking.gusmesters && booking.gusmesters.length > 0
                                ? formatGusmesterNames(booking.gusmesters.map((g) => g.name))
                                : booking.gusmester_name}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Booket den {format(new Date(booking.created_at), 'd. MMM yyyy')}</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-1">Session Information</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Traditionel dansk GUS sauna session</li>
                            <li>• Ledet af erfaren Gusmester</li>
                            <li>• Ankom venligst 10 minutter før starttidspunkt</li>
                            <li>• Medbring dit eget håndklæde og vandflaske</li>
                          </ul>
                        </div>
                      </div>

                      {/* Cancel Button */}
                      {canCancel(booking.session_date) && (
                        <div className="ml-4">
                          <button
                            onClick={() => handleCancelBooking(booking.id)}
                            disabled={cancellingId === booking.id}
                            className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              cancellingId === booking.id
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {cancellingId === booking.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                                Aflyser...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4 mr-1" />
                                Aflys Booking
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waiting List Section */}
          {waitingList.filter(entry => isWithinOneDay(entry.session_date)).length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Venteliste</h2>
              <div className="grid gap-6">
                {waitingList.filter(entry => isWithinOneDay(entry.session_date)).map((entry) => (
                  <div key={entry.id} className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {entry.session_name || 'GUS Session'} - {format(new Date(entry.session_date), 'EEEE')}
                            </h3>
                            <p className="text-gray-600">
                              {format(new Date(entry.session_date), 'EEEE, MMMM d, yyyy')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-amber-600" />
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                              På Venteliste
                            </span>
                            <span className="px-2 py-1 bg-amber-200 text-amber-900 rounded-full text-xs font-semibold">
                              Position #{entry.position}
                            </span>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>{entry.start_time} - {entry.end_time}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            <span>Gusmester: {entry.gusmester_name}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Tilsluttet den {format(new Date(entry.joined_at), 'd. MMM yyyy')}</span>
                          </div>
                        </div>

                        <div className="bg-amber-100 p-3 rounded-lg mb-4">
                          <h4 className="font-medium text-amber-900 mb-1">Venteliste Information</h4>
                          <ul className="text-sm text-amber-800 space-y-1">
                            <li>• Du er på ventelisten for denne session</li>
                            <li>• Din position på ventelisten: <strong>#{entry.position}</strong></li>
                            <li>• Du skal selv tjekke om du har fået en plads på dagen for gusen.</li>
                            <li>• Du kan forlade ventelisten når som helst</li>
                          </ul>
                        </div>
                      </div>

                      {/* Leave Waiting List Button */}
                      <div className="ml-4">
                        <button
                          onClick={() => handleLeaveWaitingList(entry.id)}
                          disabled={leavingWaitingList === entry.id}
                          className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            leavingWaitingList === entry.id
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          }`}
                        >
                          {leavingWaitingList === entry.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                              Forlader...
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Forlad Venteliste
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Booking Information */}
      <div className="mt-12 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">GUS Session Information</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <p><strong>Tidsplan:</strong> Ugentlige sessioner kl. 20:00-21:00</p>
            <p><strong>Kapacitet:</strong> Maksimum 16 personer per session</p>
            <p><strong>Månedlig Grænse:</strong> 2 sessioner per måned per medlem</p>
          </div>
          <div>
            <p><strong>Sæson:</strong> 1. september til 31. april</p>
            <p><strong>Lokation:</strong> Traditionel dansk sauna facilitet</p>
            <p><strong>Varighed:</strong> 60 minutter per session</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Bemærk:</strong> Du kan kun aflyse bookinger for fremtidige sessioner. 
            Bookinger for dagens eller tidligere sessioner kan ikke aflyses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyBookings; 